import {
  X,
  Video,
  VideoOff,
  Mic,
  MicOff,
  Eye,
  ClipboardList,
  Square,
  Loader2,
  CheckCircle2,
} from 'lucide-react';
import { useState, useRef, useEffect, useCallback, useMemo } from 'react';

import {
  ElevenLabsRealtimeClient,
  OmniVoiceRealtimeClient,
  getOmniVoiceGatewayUrl,
  isOmniVoiceEnabled,
  isOmniVoiceRelayOptIn,
  type RealtimeConnectionState,
} from '../lib/omnivoice';
import { extractBearerToken, shouldFallbackOnClose } from '../lib/omnivoice/protocol';

// A realtime voice client, regardless of provider (OmniVoice/OpenAI relay or
// ElevenLabs ConvAI). Both expose the same lifecycle + vision-context surface.
interface RealtimeClientLike {
  connect: () => Promise<void>;
  stop: () => void;
  sendVisionContext: (description: string) => void;
}

import { NexusSoulOrb, type OrbState } from './NexusSoulOrb';

const NEXUS_API_URL = import.meta.env.VITE_NEXUS_API_URL || '/nexus-api';

// Aurora DS: tło pełnoekranowej sesji głosowej — poświata violet-500
// (rgba(139,92,246,…) = --violet-500) na neutralnym --cosmos-950; kolory
// wyłącznie z tokenów zamiast literalnych hexów.
const VOICE_SESSION_BG =
  'radial-gradient(ellipse at 50% 40%, rgba(139,92,246,0.12) 0%, rgba(139,92,246,0) 60%), var(--cosmos-950)';

// Silence detection: if RMS < threshold for SILENCE_DURATION ms → user stopped talking
const SILENCE_THRESHOLD = 0.015;
const SILENCE_DURATION_MS = 1800;
// Minimum recording length before we consider sending
const MIN_RECORDING_MS = 600;
// Karaoke roller: words per line in the 3-line subtitle roller
const WORDS_PER_LINE = 5;
// Real-time video: how often we sample a camera frame for vision analysis (ms).
// A few seconds keeps it "live" without hammering the vision model.
const VISION_INTERVAL_MS = 4000;
// While RECORDING a process we sample much more often so no step is missed —
// the server dedupes near-identical frames, so a real change is what counts.
const PROCESS_VISION_INTERVAL_MS = 1500;

interface NexusVoiceSessionProps {
  tenantId: string;
  userId: string;
  conversationId: string | null;
  onConversationId: (id: string) => void;
  onClose: () => void;
  getAuthHeaders: () => Promise<Record<string, string>>;
}

/**
 * Turn-based voice surface: record → base64 → POST /api/nexus/voice/turn →
 * play MP3. This is the historical default and the fallback whenever the
 * realtime path is disabled or unavailable. Behavior is unchanged.
 */
function TurnBasedVoiceSession({
  tenantId,
  userId,
  conversationId,
  onConversationId,
  onClose,
  getAuthHeaders,
}: NexusVoiceSessionProps) {
  const [orbState, setOrbState] = useState<OrbState>('idle');
  const [audioLevel, setAudioLevel] = useState(0);
  const [transcript, setTranscript] = useState('');
  const [nexusResponse, setNexusResponse] = useState('');
  const [isMicActive, setIsMicActive] = useState(false);
  const [isCameraActive, setIsCameraActive] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Karaoke subtitle: full sentence visible, word-by-word highlight
  const [karaokeWords, setKaraokeWords] = useState<string[]>([]);
  const [activeWordIndex, setActiveWordIndex] = useState(-1);
  const [karaokeVisible, setKaraokeVisible] = useState(false);
  const karaokeTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Refs for audio pipeline
  const streamRef = useRef<MediaStream | null>(null);
  const audioCtxRef = useRef<AudioContext | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const recorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const recordingStartRef = useRef(0);
  const silenceStartRef = useRef(0);
  const isProcessingRef = useRef(false);
  const animFrameRef = useRef(0);
  const isMicActiveRef = useRef(false);

  // Refs for camera
  const videoRef = useRef<HTMLVideoElement>(null);
  const cameraStreamRef = useRef<MediaStream | null>(null);

  // Refs for playback level monitoring
  const playbackAnalyserRef = useRef<AnalyserNode | null>(null);

  // Conversation ID ref for async callbacks
  const convIdRef = useRef(conversationId);
  useEffect(() => {
    convIdRef.current = conversationId;
  }, [conversationId]);

  // ---- Audio level monitoring loop ----
  const monitorAudio = useCallback(() => {
    const analyser = orbState === 'speaking' ? playbackAnalyserRef.current : analyserRef.current;
    if (!analyser) {
      animFrameRef.current = requestAnimationFrame(monitorAudio);
      return;
    }

    const data = new Uint8Array(analyser.fftSize);
    analyser.getByteTimeDomainData(data);

    // Calculate RMS
    let sum = 0;
    for (let i = 0; i < data.length; i++) {
      const val = (data[i] - 128) / 128;
      sum += val * val;
    }
    const rms = Math.sqrt(sum / data.length);
    setAudioLevel(Math.min(1, rms * 4)); // amplify for visual effect

    // Silence detection during listening
    if (orbState === 'listening' && recorderRef.current?.state === 'recording') {
      const now = Date.now();
      const recordingDuration = now - recordingStartRef.current;

      if (rms < SILENCE_THRESHOLD) {
        if (silenceStartRef.current === 0) {
          silenceStartRef.current = now;
        } else if (
          now - silenceStartRef.current > SILENCE_DURATION_MS &&
          recordingDuration > MIN_RECORDING_MS &&
          !isProcessingRef.current
        ) {
          // Silence detected — send accumulated audio
          silenceStartRef.current = 0;
          stopAndSend();
        }
      } else {
        silenceStartRef.current = 0;
      }
    }

    animFrameRef.current = requestAnimationFrame(monitorAudio);
  }, [orbState]);

  useEffect(() => {
    animFrameRef.current = requestAnimationFrame(monitorAudio);
    return () => cancelAnimationFrame(animFrameRef.current);
  }, [monitorAudio]);

  // ---- Start microphone ----
  const startMic = useCallback(async () => {
    try {
      setError(null);
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true },
      });
      streamRef.current = stream;

      const audioCtx = new AudioContext();
      audioCtxRef.current = audioCtx;
      const source = audioCtx.createMediaStreamSource(stream);
      const analyser = audioCtx.createAnalyser();
      analyser.fftSize = 512;
      source.connect(analyser);
      analyserRef.current = analyser;

      setIsMicActive(true);
      isMicActiveRef.current = true;
      startRecording(stream);
    } catch {
      setError('Brak dostępu do mikrofonu. Sprawdź uprawnienia przeglądarki.');
    }
  }, []);

  // ---- Stop microphone ----
  const stopMic = useCallback(() => {
    isMicActiveRef.current = false;
    setIsMicActive(false);
    setOrbState('idle');
    setAudioLevel(0);

    if (recorderRef.current?.state === 'recording') {
      recorderRef.current.stop();
    }
    recorderRef.current = null;

    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;

    audioCtxRef.current?.close();
    audioCtxRef.current = null;
    analyserRef.current = null;
  }, []);

  // ---- Start / restart recording ----
  const startRecording = useCallback((stream: MediaStream) => {
    if (!isMicActiveRef.current) return;

    chunksRef.current = [];
    const mimeType = MediaRecorder.isTypeSupported('audio/webm') ? 'audio/webm' : 'audio/mp4';
    const recorder = new MediaRecorder(stream, { mimeType });

    recorder.ondataavailable = (e) => {
      if (e.data.size > 0) chunksRef.current.push(e.data);
    };

    recorder.onstop = () => {
      // Handled by stopAndSend
    };

    recorder.start(250); // collect in 250ms chunks for granularity
    recorderRef.current = recorder;
    recordingStartRef.current = Date.now();
    silenceStartRef.current = 0;
    setOrbState('listening');
  }, []);

  // ---- Stop recording and send to backend ----
  const stopAndSend = useCallback(async () => {
    if (isProcessingRef.current) return;
    isProcessingRef.current = true;

    const recorder = recorderRef.current;
    if (!recorder || recorder.state !== 'recording') {
      isProcessingRef.current = false;
      return;
    }

    // Stop recording — this fires a final ondataavailable
    recorder.stop();
    setOrbState('thinking');
    setAudioLevel(0);

    // Small delay to collect final chunk
    await new Promise((r) => setTimeout(r, 100));

    const mimeType = recorder.mimeType || 'audio/webm';
    const blob = new Blob(chunksRef.current, { type: mimeType });

    if (blob.size < 1000) {
      // Too short / empty — restart listening
      isProcessingRef.current = false;
      if (isMicActiveRef.current && streamRef.current) {
        startRecording(streamRef.current);
      }
      return;
    }

    // Convert blob to base64
    const base64 = await new Promise<string>((resolve) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve((reader.result as string).split(',')[1]);
      reader.readAsDataURL(blob);
    });

    // Capture camera frame if active
    let imageBase64: string | undefined;
    if (cameraStreamRef.current && videoRef.current) {
      const canvas = document.createElement('canvas');
      const video = videoRef.current;
      canvas.width = video.videoWidth || 640;
      canvas.height = video.videoHeight || 480;
      const ctx = canvas.getContext('2d');
      if (ctx) {
        ctx.drawImage(video, 0, 0);
        imageBase64 = canvas.toDataURL('image/jpeg', 0.7);
      }
    }

    try {
      setTranscript('');
      setNexusResponse('');

      const authHeaders = await getAuthHeaders();
      const body: Record<string, unknown> = {
        tenant_id: tenantId,
        user_id: userId,
        audio_base64: base64,
        language: 'pl',
        mime_type: mimeType,
        conversation_id: convIdRef.current || undefined,
        synthesize_response: true,
      };

      // If camera is active, include image in a combined vision+voice request
      if (imageBase64) {
        body.image_base64 = imageBase64;
      }

      const response = await fetch(`${NEXUS_API_URL}/api/nexus/voice/turn`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders },
        body: JSON.stringify(body),
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const data = await response.json();

      if (data.conversation_id) {
        convIdRef.current = data.conversation_id;
        onConversationId(data.conversation_id);
      }

      setTranscript(data.transcription || '');
      setNexusResponse(data.response || '');

      // Play response audio
      if (data.audio_response?.audio_base64) {
        await playResponse(data.audio_response.audio_base64);
      }
    } catch (err) {
      console.error('Voice turn failed:', err);
      setError('Błąd przetwarzania głosu. Spróbuję ponownie...');
      setTimeout(() => setError(null), 3000);
    } finally {
      isProcessingRef.current = false;
      // Resume listening
      if (isMicActiveRef.current && streamRef.current) {
        startRecording(streamRef.current);
      }
    }
  }, [tenantId, userId, getAuthHeaders, onConversationId, startRecording]);

  // ---- Play response audio with level monitoring ----
  const playResponse = useCallback((base64Audio: string): Promise<void> => {
    return new Promise((resolve) => {
      setOrbState('speaking');

      const audio = new Audio(`data:audio/mp3;base64,${base64Audio}`);

      // Create analyser for playback to sync orb with speech
      try {
        const ctx = new AudioContext();
        const source = ctx.createMediaElementSource(audio);
        const analyser = ctx.createAnalyser();
        analyser.fftSize = 512;
        source.connect(analyser);
        analyser.connect(ctx.destination);
        playbackAnalyserRef.current = analyser;
      } catch {
        // Fallback: just play without analyser
        playbackAnalyserRef.current = null;
      }

      audio.onended = () => {
        setOrbState(isMicActiveRef.current ? 'listening' : 'idle');
        playbackAnalyserRef.current = null;
        resolve();
      };
      audio.onerror = () => {
        setOrbState(isMicActiveRef.current ? 'listening' : 'idle');
        playbackAnalyserRef.current = null;
        resolve();
      };

      audio.play().catch(() => {
        setOrbState(isMicActiveRef.current ? 'listening' : 'idle');
        resolve();
      });
    });
  }, []);

  // ---- Camera toggle ----
  const toggleCamera = useCallback(async () => {
    if (cameraStreamRef.current) {
      cameraStreamRef.current.getTracks().forEach((t) => t.stop());
      cameraStreamRef.current = null;
      setIsCameraActive(false);
      return;
    }

    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'environment', width: { ideal: 1280 }, height: { ideal: 720 } },
      });
      cameraStreamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
      }
      setIsCameraActive(true);
    } catch {
      setError('Brak dostępu do kamery. Sprawdź uprawnienia.');
    }
  }, []);

  // ---- Cleanup on unmount ----
  useEffect(() => {
    return () => {
      streamRef.current?.getTracks().forEach((t) => t.stop());
      cameraStreamRef.current?.getTracks().forEach((t) => t.stop());
      audioCtxRef.current?.close();
      cancelAnimationFrame(animFrameRef.current);
    };
  }, []);

  // Auto-start mic when component mounts
  useEffect(() => {
    startMic();
    return () => {
      isMicActiveRef.current = false;
    };
  }, [startMic]);

  const stateLabel =
    orbState === 'listening'
      ? 'Słucham...'
      : orbState === 'thinking'
        ? 'Myślę...'
        : orbState === 'speaking'
          ? 'Mówię...'
          : 'Gotowy';

  // ---- 3-line roller: split words into lines ----
  const karaokeLines = useMemo(() => {
    const lines: string[][] = [];
    for (let i = 0; i < karaokeWords.length; i += WORDS_PER_LINE) {
      lines.push(karaokeWords.slice(i, i + WORDS_PER_LINE));
    }
    return lines;
  }, [karaokeWords]);

  const activeLineIndex = activeWordIndex >= 0 ? Math.floor(activeWordIndex / WORDS_PER_LINE) : 0;

  // ---- Karaoke subtitle effect ----
  // Full sentence appears, words highlight one by one synced with speech
  useEffect(() => {
    if (karaokeTimerRef.current) {
      clearInterval(karaokeTimerRef.current);
      karaokeTimerRef.current = null;
    }

    if (!nexusResponse) {
      setKaraokeWords([]);
      setActiveWordIndex(-1);
      setKaraokeVisible(false);
      return;
    }

    const words = nexusResponse.split(/\s+/).filter(Boolean);
    setKaraokeWords(words);
    setActiveWordIndex(-1);
    setKaraokeVisible(true);

    // Start highlighting after a brief delay
    let idx = 0;
    const startDelay = setTimeout(() => {
      karaokeTimerRef.current = setInterval(() => {
        if (idx >= words.length) {
          if (karaokeTimerRef.current) clearInterval(karaokeTimerRef.current);
          // Fade out after finishing
          setTimeout(() => {
            setKaraokeVisible(false);
            setTimeout(() => {
              setKaraokeWords([]);
              setActiveWordIndex(-1);
            }, 600);
          }, 2500);
          return;
        }
        setActiveWordIndex(idx);
        idx++;
      }, 280); // ~280ms per word — natural reading pace
    }, 200);

    return () => {
      clearTimeout(startDelay);
      if (karaokeTimerRef.current) clearInterval(karaokeTimerRef.current);
    };
  }, [nexusResponse]);

  return (
    <div
      className="fixed inset-0 z-[200] flex flex-col overflow-hidden select-none"
      style={{
        background: isCameraActive ? '#000' : VOICE_SESSION_BG,
      }}
    >
      {/* ═══════ Fullscreen camera preview (background layer) ═══════ */}
      <video
        ref={videoRef}
        autoPlay
        playsInline
        muted
        className={isCameraActive ? 'absolute inset-0 w-full h-full object-cover' : 'hidden'}
      />
      {/* Subtle vignette overlay on camera */}
      {isCameraActive && (
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            background:
              'radial-gradient(ellipse at 50% 50%, transparent 40%, rgba(0,0,0,0.5) 100%)',
          }}
        />
      )}

      {/* ═══════ Ambient glow effects (no-camera mode) ═══════ */}
      {!isCameraActive && (
        <>
          <div
            className="absolute top-1/4 left-1/2 -translate-x-1/2 w-[600px] h-[600px] rounded-full pointer-events-none"
            style={{
              // Paint poświaty orbu — rgba z tokenów: 139,92,246 = --violet-500,
              // 34,211,238 = --cyan-400, 16,185,129 = --emerald-500.
              background:
                orbState === 'listening'
                  ? 'radial-gradient(circle, rgba(139,92,246,0.12) 0%, transparent 70%)'
                  : orbState === 'thinking'
                    ? 'radial-gradient(circle, rgba(34,211,238,0.1) 0%, transparent 70%)'
                    : orbState === 'speaking'
                      ? 'radial-gradient(circle, rgba(16,185,129,0.1) 0%, transparent 70%)'
                      : 'radial-gradient(circle, rgba(139,92,246,0.06) 0%, transparent 70%)',
              transition: 'background 1s ease',
            }}
          />
        </>
      )}

      {/* ═══════ Top HUD bar ═══════ */}
      <div className="relative z-30 flex items-center justify-between px-5 py-3">
        <div className="flex items-center gap-2.5">
          <div className="relative flex items-center justify-center w-6 h-6">
            <div
              className="absolute w-2 h-2 rounded-full"
              style={{
                backgroundColor:
                  orbState === 'listening'
                    ? 'var(--violet-400)'
                    : orbState === 'thinking'
                      ? 'var(--cyan-400)'
                      : orbState === 'speaking'
                        ? 'var(--emerald-400)'
                        : 'var(--ids-text-subtle)',
                boxShadow:
                  orbState !== 'idle'
                    ? `0 0 8px 2px ${orbState === 'listening' ? 'var(--violet-400)' : orbState === 'thinking' ? 'var(--cyan-400)' : 'var(--emerald-400)'}`
                    : 'none',
                transition: 'all 0.5s ease',
              }}
            />
            {orbState !== 'idle' && (
              <div
                className="absolute w-4 h-4 rounded-full animate-ping"
                style={{
                  backgroundColor:
                    orbState === 'listening'
                      ? 'rgba(167,139,250,0.3)'
                      : orbState === 'thinking'
                        ? 'rgba(34,211,238,0.3)'
                        : 'rgba(52,211,153,0.3)',
                }}
              />
            )}
          </div>
          <span
            className="text-xs font-medium tracking-[0.2em] uppercase"
            style={{
              // Paint stanów orbu: violet-300 / cyan-300 / emerald-300 (jaśniejsze
              // odcienie tokenów --violet-400/--cyan-400/--emerald-400).
              color:
                orbState === 'listening'
                  ? '#c4b5fd'
                  : orbState === 'thinking'
                    ? '#67e8f9'
                    : orbState === 'speaking'
                      ? '#6ee7b7'
                      : 'var(--ids-text-subtle)',
              transition: 'color 0.5s ease',
            }}
          >
            {stateLabel}
          </span>
        </div>
        <button
          onClick={() => {
            stopMic();
            cameraStreamRef.current?.getTracks().forEach((t) => t.stop());
            onClose();
          }}
          className="p-2 text-white/40 hover:text-white/90 transition-colors rounded-ids-md hover:bg-white/5"
          aria-label="Zamknij"
        >
          <X className="w-5 h-5" />
        </button>
      </div>

      {/* ═══════ Center: Soul Orb ═══════ */}
      <div className="flex-1 flex items-center justify-center relative z-10">
        <div
          className="relative"
          style={{
            transform: isCameraActive ? 'scale(0.7) translateY(-20%)' : 'scale(1)',
            opacity: isCameraActive ? 0.8 : 1,
            transition: 'transform 0.5s ease, opacity 0.5s ease',
          }}
        >
          <NexusSoulOrb state={orbState} audioLevel={audioLevel} size={220} />
        </div>
      </div>

      {/* ═══════ Karaoke subtitles — 3-line roller ═══════ */}
      <div
        className="absolute inset-x-0 bottom-28 z-20 flex flex-col items-center pointer-events-none px-6"
        style={{ opacity: karaokeVisible ? 1 : 0, transition: 'opacity 0.6s ease' }}
      >
        {/* User transcript (small, dimmed) */}
        {transcript && orbState !== 'listening' && (
          <div className="mb-2 text-center animate-[fadeInUp_0.3s_ease-out]">
            <span className="text-xs text-white/20 italic font-light tracking-wide">
              &ldquo;{transcript}&rdquo;
            </span>
          </div>
        )}

        {/* AI response — 3-line roller: top fades, middle focused, bottom emerging */}
        {karaokeLines.length > 0 && (
          <div
            className="max-w-md w-full overflow-hidden relative mx-auto"
            style={{ height: '8rem' }}
          >
            <div
              className="transition-transform duration-700 ease-out"
              style={{
                transform: `translateY(${(1 - activeLineIndex) * 2.65}rem)`,
              }}
            >
              {karaokeLines.map((lineWords, lineIdx) => {
                const distance = lineIdx - activeLineIndex;
                const isCurrent = distance === 0;
                const isPrev = distance === -1;
                const isNext = distance === 1;

                return (
                  <div
                    key={lineIdx}
                    className="flex items-center justify-center gap-x-[0.35em] flex-wrap"
                    style={{
                      height: '2.65rem',
                      opacity: isCurrent ? 1 : isPrev ? 0.12 : isNext ? 0.3 : 0,
                      filter: isCurrent ? 'none' : `blur(${isPrev ? 2.5 : isNext ? 1.2 : 3}px)`,
                      transition: 'opacity 0.6s ease, filter 0.6s ease',
                    }}
                  >
                    {lineWords.map((word, wIdx) => {
                      const globalIdx = lineIdx * WORDS_PER_LINE + wIdx;
                      const isActive = globalIdx === activeWordIndex;
                      const isRead = globalIdx < activeWordIndex;

                      return (
                        <span
                          key={wIdx}
                          className="inline-block"
                          style={{
                            fontSize: isCurrent
                              ? isActive
                                ? 'clamp(1.35rem, 4.5vw, 1.7rem)'
                                : 'clamp(1.15rem, 3.8vw, 1.5rem)'
                              : 'clamp(0.9rem, 3vw, 1.15rem)',
                            fontWeight: isActive ? 800 : isCurrent ? 500 : 300,
                            lineHeight: 1.3,
                            color: isActive
                              ? '#ffffff'
                              : isRead && isCurrent
                                ? 'rgba(255,255,255,0.4)'
                                : isCurrent
                                  ? 'rgba(255,255,255,0.25)'
                                  : 'rgba(255,255,255,0.5)',
                            textShadow: isActive
                              ? '0 0 30px rgba(139,92,246,0.8), 0 0 60px rgba(139,92,246,0.3), 0 2px 8px rgba(0,0,0,0.9)'
                              : 'none',
                            transform: isActive ? 'scale(1.12)' : 'scale(1)',
                            transition: 'all 0.2s ease-out',
                          }}
                        >
                          {word}
                        </span>
                      );
                    })}
                  </div>
                );
              })}
            </div>
            {/* Gradient masks for top and bottom fade into background */}
            <div
              className="absolute top-0 left-0 right-0 pointer-events-none"
              style={{
                height: '1.5rem',
                background: isCameraActive
                  ? 'linear-gradient(to bottom, rgba(0,0,0,1) 0%, transparent 100%)'
                  : 'linear-gradient(to bottom, rgba(10,10,15,1) 0%, transparent 100%)',
              }}
            />
            <div
              className="absolute bottom-0 left-0 right-0 pointer-events-none"
              style={{
                height: '1.5rem',
                background: isCameraActive
                  ? 'linear-gradient(to top, rgba(0,0,0,1) 0%, transparent 100%)'
                  : 'linear-gradient(to top, rgba(10,10,15,1) 0%, transparent 100%)',
              }}
            />
          </div>
        )}

        {/* Error message */}
        {error && (
          <div className="mt-3 text-sm text-red-400/80 font-medium animate-pulse">{error}</div>
        )}
      </div>

      {/* ═══════ Bottom controls — glassmorphic bar ═══════ */}
      <div className="relative z-30 flex items-center justify-center gap-5 pb-6 pt-3">
        <div
          className="flex items-center gap-4 px-6 py-3 rounded-ids-xl"
          style={{
            background: 'rgba(255,255,255,0.05)',
            backdropFilter: 'blur(20px)',
            border: '1px solid rgba(255,255,255,0.08)',
          }}
        >
          {/* Camera toggle */}
          <button
            onClick={toggleCamera}
            className="relative p-3.5 rounded-ids-lg transition-all duration-300"
            style={{
              background: isCameraActive
                ? 'linear-gradient(135deg, rgba(16,185,129,0.3), rgba(16,185,129,0.15))'
                : 'rgba(255,255,255,0.05)',
              border: isCameraActive
                ? '1px solid rgba(52,211,153,0.3)'
                : '1px solid rgba(255,255,255,0.05)',
              boxShadow: isCameraActive ? '0 0 20px rgba(16,185,129,0.2)' : 'none',
            }}
            title={isCameraActive ? 'Wyłącz kamerę' : 'Włącz kamerę'}
          >
            {isCameraActive ? (
              <Video className="w-5 h-5 text-emerald-300" />
            ) : (
              <VideoOff className="w-5 h-5 text-white/40" />
            )}
          </button>

          {/* Mic toggle (hero button) */}
          <button
            onClick={isMicActive ? stopMic : startMic}
            className="relative p-5 rounded-ids-xl transition-all duration-300"
            style={{
              background: isMicActive
                ? 'linear-gradient(135deg, rgba(139,92,246,0.5), rgba(168,85,247,0.3))'
                : 'rgba(255,255,255,0.08)',
              border: isMicActive
                ? '1px solid rgba(167,139,250,0.4)'
                : '1px solid rgba(255,255,255,0.05)',
              boxShadow: isMicActive
                ? `0 0 30px rgba(139,92,246,${0.2 + audioLevel * 0.3}), 0 0 60px rgba(139,92,246,${audioLevel * 0.15})`
                : 'none',
            }}
            title={isMicActive ? 'Wyłącz mikrofon' : 'Włącz mikrofon'}
          >
            {isMicActive ? (
              <Mic className="w-7 h-7 text-purple-200" />
            ) : (
              <MicOff className="w-7 h-7 text-white/40" />
            )}
            {/* Pulsing ring when listening */}
            {isMicActive && orbState === 'listening' && (
              <div
                className="absolute inset-0 rounded-ids-xl animate-ping"
                style={{
                  border: '2px solid rgba(167,139,250,0.3)',
                  animationDuration: '2s',
                }}
              />
            )}
          </button>

          {/* End session */}
          <button
            onClick={() => {
              stopMic();
              cameraStreamRef.current?.getTracks().forEach((t) => t.stop());
              onClose();
            }}
            className="p-3.5 rounded-ids-lg transition-all duration-300"
            style={{
              background: 'rgba(239,68,68,0.15)',
              border: '1px solid rgba(239,68,68,0.2)',
            }}
            title="Zakończ rozmowę"
          >
            <X className="w-5 h-5 text-red-400" />
          </button>
        </div>
      </div>

      {/* ═══════ CSS keyframes ═══════ */}
      <style>{`
        @keyframes fadeInUp {
          from { opacity: 0; transform: translateY(12px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Realtime (OmniVoice) surface — speech-to-speech via the omnivoice-gateway.
// Only mounted when VITE_OMNIVOICE_ENABLED is on. On any setup failure it calls
// onFallback() so the parent swaps in the turn-based session — no regression.
// ---------------------------------------------------------------------------

const REALTIME_STATE_TO_ORB: Record<RealtimeConnectionState, OrbState> = {
  connecting: 'thinking',
  listening: 'listening',
  thinking: 'thinking',
  speaking: 'speaking',
  closed: 'idle',
};

function RealtimeVoiceSession({
  tenantId,
  userId,
  getAuthHeaders,
  onClose,
  onFallback,
  elevenLabsSignedUrl,
  elevenLabsDynamicVariables,
}: Pick<NexusVoiceSessionProps, 'tenantId' | 'userId' | 'getAuthHeaders' | 'onClose'> & {
  onFallback: (reason: string) => void;
  /** When set, use the ElevenLabs ConvAI client with this signed WS URL
   *  instead of the OmniVoice/OpenAI gateway relay. */
  elevenLabsSignedUrl?: string;
  /** Server-signed ConvAI dynamic variables (tenant_id/user_id/ctx_sig) from
   *  the mint response — the bridge only trusts a ctx it signed itself. */
  elevenLabsDynamicVariables?: Record<string, string>;
}) {
  const [orbState, setOrbState] = useState<OrbState>('thinking');
  const [audioLevel, setAudioLevel] = useState(0);
  const [transcript, setTranscript] = useState('');
  const [nexusResponse, setNexusResponse] = useState('');

  // Real-time video ("wideorozmowa"): optional camera that continuously feeds
  // frames to the server-side vision engine (POST /api/nexus/vision) and injects
  // the analysis into the realtime conversation so NEXUS can see and react.
  const [isCameraActive, setIsCameraActive] = useState(false);
  const [visionText, setVisionText] = useState('');
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const cameraStreamRef = useRef<MediaStream | null>(null);
  const visionIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const visionBusyRef = useRef(false);

  // Live process capture: record a process through the camera; NEXUS accumulates
  // an observation timeline (server-side, deduped) and on finish synthesizes +
  // persists a structured process.
  const [isProcessRecording, setIsProcessRecording] = useState(false);
  const [processStepCount, setProcessStepCount] = useState(0);
  const [processBusy, setProcessBusy] = useState(false);
  const [capturedProcess, setCapturedProcess] = useState<{
    title: string;
    summary: string | null;
    steps: Array<Record<string, unknown>>;
    saved: boolean;
  } | null>(null);
  // Ref mirror so the frame-capture interval reads the current recording state
  // without being re-created on every toggle.
  const processRecordingRef = useRef(false);

  const clientRef = useRef<RealtimeClientLike | null>(null);
  const onFallbackRef = useRef(onFallback);
  const onCloseRef = useRef(onClose);
  // getAuthHeaders lives in a ref (same churn-shielding pattern as
  // sessionRequestedRef in NexusVoiceSession below): a parent re-render that
  // recreates the function must NOT be a reason to tear down and reconnect a
  // live voice session — that cut NEXUS off mid-sentence and replayed the
  // greeting. The connection effect reads the latest identity via this ref.
  const getAuthHeadersRef = useRef(getAuthHeaders);
  const connectedRef = useRef(false);
  useEffect(() => {
    onFallbackRef.current = onFallback;
    onCloseRef.current = onClose;
    getAuthHeadersRef.current = getAuthHeaders;
  }, [onFallback, onClose, getAuthHeaders]);

  useEffect(() => {
    let disposed = false;

    const callbacks = {
      onStateChange: (s: RealtimeConnectionState) => setOrbState(REALTIME_STATE_TO_ORB[s]),
      onAudioLevel: (l: number) => setAudioLevel(l),
      onUserTranscript: (t: string) => {
        setTranscript(t);
        setNexusResponse('');
      },
      onAssistantTranscript: (t: string, final: boolean) => {
        if (final) setNexusResponse(t);
      },
      onClose: (code: number, reason: string) => {
        // A close before connect() resolves is handled by its rejection.
        if (!connectedRef.current || disposed) return;
        // Auth/config/upstream failure codes mean realtime is broken, not that
        // the conversation ended — switch to turn-based voice instead of
        // silently dropping the overlay. (ElevenLabs has no gateway close codes,
        // so a mid-session close there just ends the overlay.)
        if (!elevenLabsSignedUrl && shouldFallbackOnClose(code)) {
          onFallbackRef.current(`gateway closed: ${code} ${reason}`.trim());
          return;
        }
        onCloseRef.current();
      },
      onError: (err: Error) => {
        console.warn('Realtime error:', err.message);
      },
    };

    (async () => {
      let client: RealtimeClientLike;

      if (elevenLabsSignedUrl) {
        // ElevenLabs ConvAI — the signed URL was already minted server-side.
        // tenant/user go in as dynamic variables so the NEXUS custom-LLM
        // bridge answers with THIS user's data (memory, tenant, modules).
        //
        // HARD REQUIREMENT: a server-SIGNED ctx_sig. Without it the bridge
        // cannot verify the INSOLITUM_CTX and refuses every turn ("Nie widzę
        // kontekstu") — a context-blind session. Never start one; fall back to
        // the brain-backed turn-based path (which still calls chat()+recall).
        if (!elevenLabsDynamicVariables?.ctx_sig) {
          onFallbackRef.current('ElevenLabs session without signed context');
          return;
        }
        client = new ElevenLabsRealtimeClient({
          signedUrl: elevenLabsSignedUrl,
          callbacks,
          dynamicVariables: elevenLabsDynamicVariables,
        });
      } else {
        const gatewayUrl = getOmniVoiceGatewayUrl();
        if (!gatewayUrl) {
          // SEAM: realtime enabled but no gateway endpoint configured — do not
          // guess an origin; fall back to turn-based voice.
          onFallbackRef.current('VITE_OMNIVOICE_GATEWAY_URL not set');
          return;
        }
        let token: string | null = null;
        try {
          token = extractBearerToken(await getAuthHeadersRef.current());
        } catch {
          token = null;
        }
        if (disposed) return;
        if (!token) {
          onFallbackRef.current('no auth token for realtime');
          return;
        }
        client = new OmniVoiceRealtimeClient({ gatewayUrl, token, tenantId, callbacks });
      }

      clientRef.current = client;
      try {
        await client.connect();
        connectedRef.current = true;
        if (disposed) client.stop();
      } catch (err) {
        clientRef.current = null;
        if (!disposed) {
          onFallbackRef.current(err instanceof Error ? err.message : 'realtime connect failed');
        }
      }
    })();

    return () => {
      disposed = true;
      clientRef.current?.stop();
      clientRef.current = null;
    };
    // getAuthHeaders is deliberately NOT a dependency (read via
    // getAuthHeadersRef): its identity churning on parent re-renders must not
    // reconnect a live session. The remaining deps only change when the
    // session's actual target changes. (userId is no longer read here — the
    // signed dynamicVariables carry the identity — so it is not a dependency.)
  }, [tenantId, elevenLabsSignedUrl, elevenLabsDynamicVariables]);

  // Capture one camera frame → server-side vision → live caption + inject the
  // observation into the realtime session as context. Guarded so overlapping
  // ticks never stack, and fully fail-open (a bad tick just skips this frame).
  const analyzeFrame = useCallback(async () => {
    const video = videoRef.current;
    const stream = cameraStreamRef.current;
    if (!video || !stream || visionBusyRef.current) return;
    if (!video.videoWidth || !video.videoHeight) return;

    const canvas = document.createElement('canvas');
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.drawImage(video, 0, 0);
    const dataUrl = canvas.toDataURL('image/jpeg', 0.6);

    visionBusyRef.current = true;
    try {
      const authHeaders = await getAuthHeaders();
      const resp = await fetch(`${NEXUS_API_URL}/api/nexus/vision`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders },
        body: JSON.stringify({
          tenant_id: tenantId,
          user_id: userId,
          media_url: dataUrl,
          media_type: 'image',
          mime_type: 'image/jpeg',
          task: {
            type: 'analyze',
            prompt:
              'Krótko (1–2 zdania, po polsku) opisz co widać na obrazie z kamery użytkownika. Skup się na tym, co istotne dla rozmowy.',
          },
          context: { source: 'nexus_realtime_video', process_capture: processRecordingRef.current },
        }),
      });
      if (!resp.ok) return;
      const data = await resp.json();
      const desc: string =
        (data && data.analysis && typeof data.analysis.description === 'string'
          ? data.analysis.description
          : '') || '';
      if (desc.trim()) {
        setVisionText(desc.trim());
        clientRef.current?.sendVisionContext(desc);
      }
      // Reflect the server's accumulated step count while recording a process.
      const pc = data?.process_capture as { count?: number } | undefined;
      if (pc && typeof pc.count === 'number') setProcessStepCount(pc.count);
    } catch {
      /* transient (network / model) — next tick retries */
    } finally {
      visionBusyRef.current = false;
    }
  }, [getAuthHeaders, tenantId, userId]);

  const stopCamera = useCallback(() => {
    if (visionIntervalRef.current) {
      clearInterval(visionIntervalRef.current);
      visionIntervalRef.current = null;
    }
    cameraStreamRef.current?.getTracks().forEach((t) => t.stop());
    cameraStreamRef.current = null;
    if (videoRef.current) videoRef.current.srcObject = null;
    setIsCameraActive(false);
    setVisionText('');
    // Camera off ends any in-progress recording (nothing more can be observed).
    processRecordingRef.current = false;
    setIsProcessRecording(false);
  }, []);

  const toggleCamera = useCallback(async () => {
    if (cameraStreamRef.current) {
      stopCamera();
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'environment', width: { ideal: 1280 }, height: { ideal: 720 } },
      });
      cameraStreamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play().catch(() => undefined);
      }
      setIsCameraActive(true);
      visionIntervalRef.current = setInterval(() => {
        void analyzeFrame();
      }, VISION_INTERVAL_MS);
      // Analyze the first frame promptly (give the sensor a beat to expose).
      setTimeout(() => void analyzeFrame(), 800);
    } catch (err) {
      console.warn('NEXUS video: camera access denied:', err);
      setVisionText('Brak dostępu do kamery');
    }
  }, [analyzeFrame, stopCamera]);

  // Start recording a process: ensure the camera is on, open a server-side
  // capture session, and sample frames faster so no step is missed.
  const startProcessRecording = useCallback(async () => {
    if (!cameraStreamRef.current) {
      await toggleCamera();
      if (!cameraStreamRef.current) return; // camera denied — abort
    }
    const productName =
      window.prompt('Nazwa procesu (opcjonalnie), np. „Wyrób ciasta na chleb”:')?.trim() ||
      undefined;
    try {
      const authHeaders = await getAuthHeaders();
      await fetch(`${NEXUS_API_URL}/api/nexus/process-capture/start`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders },
        body: JSON.stringify({
          tenant_id: tenantId,
          user_id: userId,
          mode: 'general',
          product_name: productName,
        }),
      });
      processRecordingRef.current = true;
      setIsProcessRecording(true);
      setProcessStepCount(0);
      setCapturedProcess(null);
      // Switch to the denser recording cadence.
      if (visionIntervalRef.current) clearInterval(visionIntervalRef.current);
      visionIntervalRef.current = setInterval(() => {
        void analyzeFrame();
      }, PROCESS_VISION_INTERVAL_MS);
      void analyzeFrame();
    } catch (err) {
      console.warn('NEXUS process capture: start failed:', err);
    }
  }, [getAuthHeaders, tenantId, userId, analyzeFrame, toggleCamera]);

  // Finish recording: synthesize + persist the process, then show the result.
  const finishProcessRecording = useCallback(async () => {
    processRecordingRef.current = false;
    setIsProcessRecording(false);
    setProcessBusy(true);
    // Restore the normal live cadence (camera stays on).
    if (visionIntervalRef.current) clearInterval(visionIntervalRef.current);
    visionIntervalRef.current = cameraStreamRef.current
      ? setInterval(() => {
          void analyzeFrame();
        }, VISION_INTERVAL_MS)
      : null;
    try {
      const authHeaders = await getAuthHeaders();
      const resp = await fetch(`${NEXUS_API_URL}/api/nexus/process-capture/finalize`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders },
        body: JSON.stringify({ tenant_id: tenantId, user_id: userId }),
      });
      const data = await resp.json().catch(() => null);
      if (resp.ok && data?.ok) {
        setCapturedProcess({
          title: typeof data.title === 'string' ? data.title : 'Zarejestrowany proces',
          summary: typeof data.summary === 'string' ? data.summary : null,
          steps: Array.isArray(data.steps) ? data.steps : [],
          saved: true,
        });
      } else {
        setCapturedProcess({
          title: 'Nie zapisano',
          summary:
            (data && typeof data.message === 'string' && data.message) ||
            'Brak obserwacji do zapisania — pokaż proces kamerą i spróbuj ponownie.',
          steps: [],
          saved: false,
        });
      }
    } catch (err) {
      console.warn('NEXUS process capture: finalize failed:', err);
      setCapturedProcess({
        title: 'Błąd zapisu',
        summary: 'Nie udało się zapisać procesu. Spróbuj ponownie.',
        steps: [],
        saved: false,
      });
    } finally {
      setProcessBusy(false);
    }
  }, [getAuthHeaders, tenantId, userId, analyzeFrame]);

  // Stop the camera when the surface unmounts (independent of the socket teardown).
  useEffect(() => {
    return () => {
      if (visionIntervalRef.current) clearInterval(visionIntervalRef.current);
      cameraStreamRef.current?.getTracks().forEach((t) => t.stop());
      cameraStreamRef.current = null;
    };
  }, []);

  const stateLabel =
    orbState === 'listening'
      ? 'Słucham...'
      : orbState === 'thinking'
        ? 'Łączę...'
        : orbState === 'speaking'
          ? 'Mówię...'
          : 'Gotowy';

  return (
    <div
      className="fixed inset-0 z-[200] flex flex-col overflow-hidden select-none"
      style={{ background: VOICE_SESSION_BG }}
    >
      {/* Top HUD */}
      <div className="relative z-30 flex items-center justify-between px-5 py-3">
        <span
          className="text-xs font-medium tracking-[0.2em] uppercase"
          style={{
            // Paint stanów orbu: violet-300 / cyan-300 / emerald-300 (jaśniejsze
            // odcienie tokenów --violet-400/--cyan-400/--emerald-400).
            color:
              orbState === 'listening'
                ? '#c4b5fd'
                : orbState === 'thinking'
                  ? '#67e8f9'
                  : orbState === 'speaking'
                    ? '#6ee7b7'
                    : 'var(--ids-text-subtle)',
            transition: 'color 0.5s ease',
          }}
        >
          {stateLabel}
          <span className="ml-2 text-white/25 tracking-normal normal-case">
            {elevenLabsSignedUrl ? 'realtime · ElevenLabs' : 'realtime'}
          </span>
        </span>
        <button
          onClick={() => {
            clientRef.current?.stop();
            onClose();
          }}
          className="p-2 text-white/40 hover:text-white/90 transition-colors rounded-ids-md hover:bg-white/5"
          aria-label="Zamknij"
        >
          <X className="w-5 h-5" />
        </button>
      </div>

      {/* Center orb */}
      <div className="flex-1 flex items-center justify-center relative z-10">
        <NexusSoulOrb state={orbState} audioLevel={audioLevel} size={220} />
      </div>

      {/* Live camera PIP (wideorozmowa). Always mounted so the ref is stable for
          getUserMedia; only visible while the camera is active. */}
      <video
        ref={videoRef}
        autoPlay
        playsInline
        muted
        className={`absolute z-20 bottom-28 right-4 w-28 h-40 rounded-ids-xl object-cover ${
          isCameraActive ? '' : 'hidden'
        }`}
        style={{
          border: '1px solid rgba(167,139,250,0.4)',
          boxShadow: '0 0 24px rgba(139,92,246,0.25)',
        }}
      />

      {/* Subtitles */}
      <div className="absolute inset-x-0 bottom-28 z-20 flex flex-col items-center pointer-events-none px-6">
        {isProcessRecording && (
          <div className="mb-2 flex items-center gap-2 justify-center">
            <span className="w-2 h-2 rounded-full bg-red-500 animate-pulse" />
            <span className="text-xs text-red-200/90 font-medium tracking-wide">
              Nagrywam proces — {processStepCount} {processStepCount === 1 ? 'krok' : 'kroków'}
            </span>
          </div>
        )}
        {isCameraActive && visionText && (
          <div className="mb-3 max-w-md w-full flex items-start gap-2 justify-center">
            <Eye className="w-4 h-4 mt-0.5 shrink-0 text-cyan-300/80" />
            <span className="text-sm text-cyan-100/80 font-light leading-snug text-center">
              {visionText}
            </span>
          </div>
        )}
        {transcript && (
          <div className="mb-2 text-center">
            <span className="text-xs text-white/25 italic font-light tracking-wide">
              &ldquo;{transcript}&rdquo;
            </span>
          </div>
        )}
        {nexusResponse && (
          <div className="max-w-md w-full text-center">
            <span className="text-lg text-white/90 font-medium leading-snug">{nexusResponse}</span>
          </div>
        )}
      </div>

      {/* Captured-process result panel */}
      {capturedProcess && (
        <div className="absolute inset-0 z-40 flex items-center justify-center bg-black/70 p-6 backdrop-blur-sm">
          <div className="card shadow-elev-4 relative max-h-[80vh] w-full max-w-lg overflow-y-auto border-aurora-border-strong bg-aurora-raised p-6">
            <button
              onClick={() => setCapturedProcess(null)}
              className="absolute top-3 right-3 rounded-ids-md bg-aurora-surface p-1.5 text-aurora-muted transition-colors hover:text-aurora-text"
              title="Zamknij"
              aria-label="Zamknij"
            >
              <X className="w-4 h-4" />
            </button>
            <div className="flex items-center gap-2 mb-1 pr-8">
              {capturedProcess.saved ? (
                <CheckCircle2 className="w-5 h-5 shrink-0 text-aurora-success" />
              ) : (
                <X className="w-5 h-5 shrink-0 text-aurora-danger" />
              )}
              <h3 className="display text-lg font-semibold text-aurora-text">
                {capturedProcess.title}
              </h3>
            </div>
            {capturedProcess.saved && (
              <p className="text-xs text-aurora-success mb-3">
                Zapisano w systemie — {capturedProcess.steps.length}{' '}
                {capturedProcess.steps.length === 1 ? 'krok' : 'kroków'}
              </p>
            )}
            {capturedProcess.summary && (
              <p className="text-sm text-aurora-muted mb-4 leading-snug">
                {capturedProcess.summary}
              </p>
            )}
            {capturedProcess.steps.length > 0 && (
              <ol className="space-y-2">
                {capturedProcess.steps.map((s, i) => {
                  const step = s as { title?: string; description?: string; phase?: string };
                  return (
                    <li key={i} className="flex gap-3 text-sm">
                      <span
                        className="mono shrink-0 w-6 h-6 rounded-full flex items-center justify-center text-xs font-semibold"
                        style={{
                          background: 'rgba(var(--ids-brand-rgb),0.2)',
                          color: 'var(--ids-brand)',
                        }}
                      >
                        {i + 1}
                      </span>
                      <div>
                        <div className="text-aurora-text font-medium">
                          {step.title || `Krok ${i + 1}`}
                          {step.phase ? (
                            <span className="ml-2 text-[10px] uppercase tracking-wide text-aurora-brand">
                              {step.phase}
                            </span>
                          ) : null}
                        </div>
                        {step.description && (
                          <div className="text-aurora-muted leading-snug">{step.description}</div>
                        )}
                      </div>
                    </li>
                  );
                })}
              </ol>
            )}
          </div>
        </div>
      )}

      {/* Bottom bar */}
      <div className="relative z-30 flex items-center justify-center gap-5 pb-6 pt-3">
        <div
          className="flex items-center gap-4 px-6 py-3 rounded-ids-xl"
          style={{
            background: 'rgba(255,255,255,0.05)',
            backdropFilter: 'blur(20px)',
            border: '1px solid rgba(255,255,255,0.08)',
          }}
        >
          <div
            className="relative p-5 rounded-ids-xl"
            style={{
              background: 'linear-gradient(135deg, rgba(139,92,246,0.5), rgba(168,85,247,0.3))',
              border: '1px solid rgba(167,139,250,0.4)',
              boxShadow: `0 0 30px rgba(139,92,246,${0.2 + audioLevel * 0.3})`,
            }}
            title="Rozmowa na żywo"
          >
            <Mic className="w-7 h-7 text-purple-200" />
          </div>
          <button
            onClick={() => void toggleCamera()}
            className="p-3.5 rounded-ids-lg transition-all duration-300"
            style={{
              background: isCameraActive ? 'rgba(139,92,246,0.25)' : 'rgba(255,255,255,0.05)',
              border: isCameraActive
                ? '1px solid rgba(167,139,250,0.45)'
                : '1px solid rgba(255,255,255,0.1)',
            }}
            title={isCameraActive ? 'Wyłącz kamerę' : 'Włącz kamerę — analiza wideo na żywo'}
          >
            {isCameraActive ? (
              <Video className="w-5 h-5 text-purple-200" />
            ) : (
              <VideoOff className="w-5 h-5 text-white/50" />
            )}
          </button>
          <button
            onClick={() =>
              void (isProcessRecording ? finishProcessRecording() : startProcessRecording())
            }
            disabled={processBusy}
            className="p-3.5 rounded-ids-lg transition-all duration-300 disabled:opacity-60"
            style={{
              background: isProcessRecording ? 'rgba(239,68,68,0.25)' : 'rgba(255,255,255,0.05)',
              border: isProcessRecording
                ? '1px solid rgba(248,113,113,0.5)'
                : '1px solid rgba(255,255,255,0.1)',
            }}
            title={
              isProcessRecording
                ? 'Zakończ i zapisz proces'
                : 'Nagraj proces — pokaż czynność kamerą, NEXUS zapisze wszystkie kroki'
            }
          >
            {processBusy ? (
              <Loader2 className="w-5 h-5 text-purple-200 animate-spin" />
            ) : isProcessRecording ? (
              <Square className="w-5 h-5 text-red-300" />
            ) : (
              <ClipboardList className="w-5 h-5 text-white/50" />
            )}
          </button>
          <button
            onClick={() => {
              clientRef.current?.stop();
              onClose();
            }}
            className="p-3.5 rounded-ids-lg transition-all duration-300"
            style={{
              background: 'rgba(239,68,68,0.15)',
              border: '1px solid rgba(239,68,68,0.2)',
            }}
            title="Zakończ rozmowę"
          >
            <X className="w-5 h-5 text-red-400" />
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * Voice surface entry point (the component the shell renders). Chooses the
 * realtime OmniVoice path when VITE_OMNIVOICE_ENABLED is on, and falls back to
 * the turn-based session when the flag is off OR the realtime connection fails.
 * With the flag unset this is EXACTLY the historical turn-based behavior.
 */
type VoiceMode =
  | { kind: 'pending' }
  | { kind: 'realtime-elevenlabs'; signedUrl: string; dynamicVariables?: Record<string, string> }
  | { kind: 'realtime-openai' }
  | { kind: 'turn' };

export function NexusVoiceSession(props: NexusVoiceSessionProps) {
  // GATE: no session connects until the backend says which realtime provider is
  // active. Starting the historical OpenAI session immediately and swapping to
  // ElevenLabs after the fetch produced TWO overlapping live sessions (two
  // voices talking over each other) — so 'pending' renders only a backdrop.
  const [mode, setMode] = useState<VoiceMode>({ kind: 'pending' });
  // User-facing notice shown briefly over the turn-based surface after a
  // realtime→turn-based fallback, so the switch is not silent.
  const [fallbackNotice, setFallbackNotice] = useState<string | null>(null);

  const { getAuthHeaders, tenantId: mintTenantId } = props;
  const sessionRequestedRef = useRef(false);
  useEffect(() => {
    // Mint exactly once per overlay open (getAuthHeaders identity may churn).
    if (sessionRequestedRef.current) return;
    sessionRequestedRef.current = true;
    let disposed = false;
    // Fallback when the mint is slow/unreachable OR returns a provider we do not
    // start realtime for. Prefer the BRAIN-BACKED turn-based path (STT →
    // engine.chat() with recall/tenant data → TTS). The OpenAI realtime relay is
    // brain-LESS today (no persona/recall injected into the session), so silently
    // degrading to it is the "voice nie widzi danych" symptom — it is only used
    // when an operator explicitly opts in via VITE_OMNIVOICE_RELAY_ENABLED.
    // FOLLOW-UP (out of scope here): make the omnivoice-gateway OpenAI relay
    // tenant-aware — inject persona + recall into session.instructions and expose
    // a recall tool — then this default can prefer the relay again.
    const historicalDefault = (): VoiceMode =>
      isOmniVoiceEnabled() && isOmniVoiceRelayOptIn()
        ? { kind: 'realtime-openai' }
        : { kind: 'turn' };
    // If the backend is slow/unreachable, fall back rather than leaving the user
    // staring at the pending orb. 12s (was 8s, was 3.5s): the ElevenLabs mint
    // makes three sequential upstream calls (agent exists? → sync → sign) and its
    // own comment admits it routinely needs >3.5s; at 8s the timer still raced
    // the mint and started the fallback session (different voice/persona) even
    // though the intended provider was about to answer. The pending overlay
    // renders the thinking orb, so a few extra seconds of waiting is far cheaper
    // than a mid-conversation persona mismatch — and a genuinely dead backend
    // still falls back (now to the brain-backed turn-based path).
    const timer = setTimeout(() => {
      if (!disposed) setMode((m) => (m.kind === 'pending' ? historicalDefault() : m));
    }, 12000);
    (async () => {
      try {
        const authHeaders = await getAuthHeaders();
        const resp = await fetch(`${NEXUS_API_URL}/api/nexus/voice/realtime/session`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', ...authHeaders },
          // tenant_id lets the backend verify membership and sign the
          // tenant/user pair (ctx_sig) that the ConvAI bridge will demand.
          body: JSON.stringify({ tenant_id: mintTenantId }),
        });
        if (disposed) return;
        const data = resp.ok
          ? ((await resp.json()) as {
              provider?: string;
              signed_url?: string;
              dynamic_variables?: Record<string, string>;
            })
          : {};
        if (disposed) return;
        // RACE GUARD: if the mint answered AFTER the fallback timer already
        // started a session (mode left 'pending'), keep the running session —
        // switching providers now would kill it mid-utterance and restart the
        // conversation with a different voice/persona. The minted provider
        // simply loses this round; the next overlay open tries it again.
        const resolveMode = (next: VoiceMode) =>
          setMode((m) => {
            if (m.kind !== 'pending') {
              if (m.kind !== next.kind) {
                console.warn(
                  `NEXUS voice: provider mint resolved '${next.kind}' after fallback already started '${m.kind}' — keeping the active session for its lifetime`
                );
              }
              return m;
            }
            return next;
          });
        if (data.provider === 'elevenlabs' && data.signed_url && data.dynamic_variables?.ctx_sig) {
          resolveMode({
            kind: 'realtime-elevenlabs',
            signedUrl: data.signed_url,
            dynamicVariables: data.dynamic_variables,
          });
        } else if (data.provider === 'elevenlabs') {
          // ElevenLabs minted but WITHOUT a signed INSOLITUM_CTX (no ctx_sig) —
          // the custom-LLM bridge would refuse every turn. Use the brain-backed
          // turn-based path instead of a context-blind session.
          console.warn(
            'NEXUS voice: ElevenLabs mint lacked a signed ctx_sig — using turn-based voice'
          );
          resolveMode({ kind: 'turn' });
        } else if (data.provider === 'self_hosted_turn') {
          resolveMode({ kind: 'turn' });
        } else {
          resolveMode(historicalDefault());
        }
      } catch {
        // Same guard: never clobber a session the timeout already started.
        if (!disposed) setMode((m) => (m.kind === 'pending' ? historicalDefault() : m));
      } finally {
        clearTimeout(timer);
      }
    })();
    return () => {
      disposed = true;
      clearTimeout(timer);
    };
  }, [getAuthHeaders, mintTenantId]);

  useEffect(() => {
    if (!fallbackNotice) return;
    const t = setTimeout(() => setFallbackNotice(null), 5000);
    return () => clearTimeout(t);
  }, [fallbackNotice]);

  const handleFallback = useCallback((reason: string) => {
    console.warn('Realtime voice unavailable, using turn-based voice:', reason);
    setFallbackNotice('Tryb realtime niedostępny — przełączam na klasyczny');
    setMode({ kind: 'turn' });
  }, []);

  if (mode.kind === 'pending') {
    return (
      <div
        className="fixed inset-0 z-[200] flex items-center justify-center"
        style={{ background: VOICE_SESSION_BG }}
      >
        <NexusSoulOrb state="thinking" audioLevel={0} size={220} />
      </div>
    );
  }

  if (mode.kind === 'realtime-elevenlabs' || mode.kind === 'realtime-openai') {
    return (
      <RealtimeVoiceSession
        tenantId={props.tenantId}
        userId={props.userId}
        getAuthHeaders={props.getAuthHeaders}
        onClose={props.onClose}
        onFallback={handleFallback}
        elevenLabsSignedUrl={mode.kind === 'realtime-elevenlabs' ? mode.signedUrl : undefined}
        elevenLabsDynamicVariables={
          mode.kind === 'realtime-elevenlabs' ? mode.dynamicVariables : undefined
        }
      />
    );
  }

  return (
    <>
      <TurnBasedVoiceSession {...props} />
      {fallbackNotice && (
        <div className="fixed top-16 inset-x-0 z-[210] flex justify-center pointer-events-none px-6">
          <div className="toast" style={{ borderColor: 'rgba(var(--ids-warning-rgb),0.4)' }}>
            <span className="toast__txt" style={{ color: 'var(--ids-warning)' }}>
              {fallbackNotice}
            </span>
          </div>
        </div>
      )}
    </>
  );
}
